import React, { useState, useEffect } from 'react';
import { itinerariesService } from '../../services/itinerariesService';
import SupplierPayments from './SupplierPayments';
import ClientPayments from './ClientPayments';
import { useResponsive } from '../../hooks/useResponsive';
import travelieTheme from '../../styles/travelieTheme';
import Modal from '../common/Modal';
import './EditItineraryModal.css';
// Itineraries are a straight JSON copy of a quote's components (see
// backend/routes/itineraries.js POST /convert-quote/:quoteId), so component
// editing here must use the exact same forms/types/save logic as
// QuoteBuilder.js - otherwise editing a component here silently discards
// its real data. See componentFormRegistry.js for details.
import {
  componentCategories,
  componentTypes,
  getComponentFormInitialData,
  renderComponentTypeForm,
  buildSavedComponent
} from '../Quotes/componentFormRegistry';
import '../Quotes/components/ComponentForms.css';

// Portal Settings Tab - May 6, 2026
// Phase 5.3: Migrated to TravelieTheme - June 16, 2026

const EditItineraryModal = ({ isOpen, onClose, itinerary, onUpdate, initialTab = 'details' }) => {
  const { isMobile } = useResponsive();
  const [formData, setFormData] = useState({
    title: '',
    components: [],
    options: [],
    travel_start_date: '',
    travel_end_date: '',
    total_amount: 0,
    agent_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [portalChecklist, setPortalChecklist] = useState([]);
  const [portalUrl, setPortalUrl] = useState('');
  const [sendingPortalLink, setSendingPortalLink] = useState(false);
  const [portalSchedule, setPortalSchedule] = useState([]);
  const [useDetailedSchedule, setUseDetailedSchedule] = useState(false);

  // Update active tab when initialTab changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, isOpen]);

  useEffect(() => {
    if (itinerary && isOpen) {
      // Parse components and options if they're strings
      let components = [];
      let options = [];
      
      try {
        if (itinerary.components) {
          components = typeof itinerary.components === 'string' 
            ? JSON.parse(itinerary.components) 
            : itinerary.components || [];
        }
      } catch (e) {
        console.error('Error parsing components:', e);
      }
      
      try {
        if (itinerary.options) {
          options = typeof itinerary.options === 'string' 
            ? JSON.parse(itinerary.options) 
            : itinerary.options || [];
        }
      } catch (e) {
        console.error('Error parsing options:', e);
      }

      setFormData({
        title: itinerary.title || '',
        components: Array.isArray(components) ? components : [],
        options: Array.isArray(options) ? options : [],
        travel_start_date: itinerary.travel_start_date ? 
          new Date(itinerary.travel_start_date).toISOString().split('T')[0] : '',
        travel_end_date: itinerary.travel_end_date ? 
          new Date(itinerary.travel_end_date).toISOString().split('T')[0] : '',
        total_amount: parseFloat(itinerary.total_amount) || 0,
        agent_notes: itinerary.agent_notes || ''
      });

      // Fetch portal data if quote_id exists
      if (itinerary.quote_id) {
        fetchPortalData(itinerary.quote_id);
      } else {
        setPortalUrl('');
        setPortalChecklist(getDefaultChecklist());
      }
    }
  }, [itinerary, isOpen]);

  const getDefaultChecklist = () => {
    return [
      {
        category: 'Important Documents',
        items: [
          { id: 1, text: 'Valid passport (check expiration date)', completed: false },
          { id: 2, text: 'Visa requirements confirmed', completed: false },
          { id: 3, text: 'Travel insurance purchased', completed: false },
          { id: 4, text: 'Copies of important documents made', completed: false },
        ]
      },
      {
        category: 'To-Do Before Departure',
        items: [
          { id: 5, text: 'Notify bank and credit card companies of travel', completed: false },
          { id: 6, text: 'Arrange pet/house sitter', completed: false },
          { id: 7, text: 'Hold mail delivery', completed: false },
          { id: 8, text: 'Check-in for flights 24 hours before', completed: false },
          { id: 9, text: 'Download offline maps and travel apps', completed: false },
          { id: 10, text: 'Confirm hotel reservations', completed: false },
        ]
      },
      {
        category: 'Packing List',
        items: [
          { id: 11, text: 'Medications and prescriptions', completed: false },
          { id: 12, text: 'Phone chargers and adapters', completed: false },
          { id: 13, text: 'Comfortable walking shoes', completed: false },
          { id: 14, text: 'Weather-appropriate clothing', completed: false },
          { id: 15, text: 'Sunscreen and toiletries', completed: false },
          { id: 16, text: 'Camera or phone for photos', completed: false },
        ]
      }
    ];
  };

  const fetchPortalData = async (quoteId) => {
    try {
      const response = await fetch(`/api/quotes/${quoteId}/portal-info`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPortalUrl(data.portal_url || '');
        setPortalChecklist(data.checklist || getDefaultChecklist());
        setPortalSchedule(data.schedule || []);
        setUseDetailedSchedule(data.schedule && data.schedule.length > 0);
      } else {
        // No portal yet, use defaults
        setPortalUrl('');
        setPortalChecklist(getDefaultChecklist());
        setPortalSchedule([]);
        setUseDetailedSchedule(false);
      }
    } catch (error) {
      console.error('Error fetching portal data:', error);
      setPortalUrl('');
      setPortalChecklist(getDefaultChecklist());
      setPortalSchedule([]);
      setUseDetailedSchedule(false);
    }
  };

  const calculateTotal = () => {
    const componentTotal = formData.components.reduce((sum, comp) => sum + (parseFloat(comp.price) || 0), 0);
    const optionTotal = formData.options.reduce((sum, opt) => {
      const optComponents = opt.components || [];
      return sum + optComponents.reduce((optSum, comp) => optSum + (parseFloat(comp.price) || 0), 0);
    }, 0);
    return componentTotal + (formData.options.length > 0 ? optionTotal : 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updateData = {
        title: formData.title,
        components: JSON.stringify(formData.components),
        options: JSON.stringify(formData.options),
        travel_start_date: formData.travel_start_date,
        travel_end_date: formData.travel_end_date,
        total_amount: calculateTotal(),
        agent_notes: formData.agent_notes
      };

      await itinerariesService.update(itinerary.id, updateData);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating itinerary:', err);
      setError('Failed to update itinerary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComponent = () => {
    setEditingComponent(null);
    setShowComponentModal(true);
  };

  const handleEditComponent = (component, index) => {
    setEditingComponent({ ...component, index });
    setShowComponentModal(true);
  };

  const handleSaveComponent = (componentData) => {
    // Strip the index we stashed on editingComponent (see handleEditComponent)
    // before handing it to buildSavedComponent, so it doesn't leak into the
    // saved component's stored fields.
    const { index, ...editingComponentWithoutIndex } = editingComponent || {};
    const savedComponent = buildSavedComponent({
      componentData,
      selectedComponentType: componentData.type,
      editingComponent: editingComponent ? editingComponentWithoutIndex : null
    });

    const newComponents = [...formData.components];
    if (editingComponent && editingComponent.index !== undefined) {
      newComponents[editingComponent.index] = savedComponent;
    } else {
      newComponents.push(savedComponent);
    }
    setFormData({ ...formData, components: newComponents });
    setShowComponentModal(false);
    setEditingComponent(null);
  };

  const handleDeleteComponent = (index) => {
    const newComponents = formData.components.filter((_, i) => i !== index);
    setFormData({ ...formData, components: newComponents });
  };

  const handleAddOption = () => {
    setEditingOption(null);
    setShowOptionModal(true);
  };

  const handleEditOption = (option, index) => {
    setEditingOption({ ...option, index });
    setShowOptionModal(true);
  };

  const handleSaveOption = (optionData) => {
    const newOptions = [...formData.options];
    if (editingOption && editingOption.index !== undefined) {
      newOptions[editingOption.index] = optionData;
    } else {
      newOptions.push(optionData);
    }
    setFormData({ ...formData, options: newOptions });
    setShowOptionModal(false);
  };

  const handleDeleteOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  // Portal Settings Handlers
  const handleSendPortalLink = async () => {
    if (!itinerary.quote_id) {
      alert('This itinerary is not linked to a quote.');
      return;
    }

    setSendingPortalLink(true);
    try {
      const response = await fetch(`/api/public/portal/send/${itinerary.quote_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to send portal link');
      }

      const result = await response.json();
      setPortalUrl(result.portal_url);
      alert('Portal link sent successfully!');
    } catch (error) {
      console.error('Error sending portal link:', error);
      alert('Failed to send portal link. Please try again.');
    } finally {
      setSendingPortalLink(false);
    }
  };

  const handleCopyPortalLink = () => {
    if (portalUrl) {
      navigator.clipboard.writeText(portalUrl);
      alert('Portal link copied to clipboard!');
    }
  };

  const handlePreviewPortal = () => {
    if (portalUrl) {
      window.open(portalUrl, '_blank');
    }
  };

  const handleAddChecklistItem = (categoryIndex) => {
    const newChecklist = [...portalChecklist];
    const maxId = Math.max(...newChecklist.flatMap(cat => cat.items.map(item => item.id)), 0);
    newChecklist[categoryIndex].items.push({
      id: maxId + 1,
      text: '',
      completed: false
    });
    setPortalChecklist(newChecklist);
  };

  const handleUpdateChecklistItem = (categoryIndex, itemIndex, text) => {
    const newChecklist = [...portalChecklist];
    newChecklist[categoryIndex].items[itemIndex].text = text;
    setPortalChecklist(newChecklist);
  };

  const handleDeleteChecklistItem = (categoryIndex, itemIndex) => {
    const newChecklist = [...portalChecklist];
    newChecklist[categoryIndex].items.splice(itemIndex, 1);
    setPortalChecklist(newChecklist);
  };

  const handleAddChecklistCategory = () => {
    const newChecklist = [...portalChecklist];
    newChecklist.push({
      category: 'New Category',
      items: []
    });
    setPortalChecklist(newChecklist);
  };

  const handleUpdateCategoryName = (categoryIndex, name) => {
    const newChecklist = [...portalChecklist];
    newChecklist[categoryIndex].category = name;
    setPortalChecklist(newChecklist);
  };

  const handleDeleteCategory = (categoryIndex) => {
    if (confirm('Delete this entire category and all its items?')) {
      const newChecklist = portalChecklist.filter((_, i) => i !== categoryIndex);
      setPortalChecklist(newChecklist);
    }
  };

  // Schedule handlers
  const handleAddScheduleDay = () => {
    const newDay = {
      date: '',
      title: '',
      photo: '',
      events: []
    };
    setPortalSchedule([...portalSchedule, newDay]);
  };

  const handleUpdateScheduleDay = (dayIndex, field, value) => {
    const newSchedule = [...portalSchedule];
    newSchedule[dayIndex][field] = value;
    setPortalSchedule(newSchedule);
  };

  const handleDeleteScheduleDay = (dayIndex) => {
    if (confirm('Delete this day from the schedule?')) {
      const newSchedule = portalSchedule.filter((_, i) => i !== dayIndex);
      setPortalSchedule(newSchedule);
    }
  };

  const handleAddScheduleEvent = (dayIndex) => {
    const newSchedule = [...portalSchedule];
    newSchedule[dayIndex].events.push({ time: '', description: '' });
    setPortalSchedule(newSchedule);
  };

  const handleUpdateScheduleEvent = (dayIndex, eventIndex, field, value) => {
    const newSchedule = [...portalSchedule];
    newSchedule[dayIndex].events[eventIndex][field] = value;
    setPortalSchedule(newSchedule);
  };

  const handleDeleteScheduleEvent = (dayIndex, eventIndex) => {
    const newSchedule = [...portalSchedule];
    newSchedule[dayIndex].events = newSchedule[dayIndex].events.filter((_, i) => i !== eventIndex);
    setPortalSchedule(newSchedule);
  };

  const handleSavePortalSettings = async () => {
    if (!itinerary.quote_id) {
      alert('This itinerary is not linked to a quote.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/quotes/${itinerary.quote_id}/portal-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          checklist: portalChecklist,
          schedule: useDetailedSchedule ? portalSchedule : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save portal settings');
      }

      alert('Portal settings saved successfully!');
    } catch (error) {
      console.error('Error saving portal settings:', error);
      alert('Failed to save portal settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function for button styling
  const getButtonStyle = (type) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      whiteSpace: 'nowrap'
    };

    const styles = {
      primary: {
        ...baseStyle,
        background: travelieTheme.colors.primary.main,
        color: '#FFFFFF'
      },
      secondary: {
        ...baseStyle,
        background: travelieTheme.colors.neutral.border,
        color: travelieTheme.colors.text.primary
      },
      success: {
        ...baseStyle,
        background: travelieTheme.colors.status.success,
        color: '#FFFFFF'
      },
      danger: {
        ...baseStyle,
        background: travelieTheme.colors.status.error,
        color: '#FFFFFF'
      },
      warning: {
        ...baseStyle,
        background: travelieTheme.colors.status.warning,
        color: '#FFFFFF'
      },
      info: {
        ...baseStyle,
        background: '#3B9EFF',
        color: '#FFFFFF'
      }
    };

    return styles[type] || styles.primary;
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Itinerary Details" maxWidth="900px">
        <div style={{
          display: 'flex',
          gap: travelieTheme.spacing.xs,
          padding: travelieTheme.spacing.md,
          borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
          overflowX: 'auto',
          flexShrink: 0
        }}>
          {[
            { key: 'details', label: 'Details' },
            { key: 'components', label: `Components (${formData.components.length})` },
            { key: 'options', label: `Options (${formData.options.length})` },
            { key: 'supplier-payments', label: 'Supplier Payments' },
            { key: 'client-payments', label: 'Client Payments' },
            { key: 'portal-settings', label: '🔗 Portal Settings' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                border: 'none',
                borderRadius: travelieTheme.components.card.borderRadius,
                cursor: 'pointer',
                transition: `all ${travelieTheme.transitions.fast}`,
                whiteSpace: 'nowrap',
                background: activeTab === tab.key ? travelieTheme.colors.primary.main : 'transparent',
                color: activeTab === tab.key ? '#FFFFFF' : travelieTheme.colors.text.secondary
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.key) {
                  e.currentTarget.style.background = travelieTheme.colors.neutral.background;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.key) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{
            margin: travelieTheme.spacing.md,
            padding: travelieTheme.spacing.md,
            background: '#FEF2F2',
            border: `1px solid ${travelieTheme.colors.status.error}`,
            borderRadius: travelieTheme.components.card.borderRadius,
            color: travelieTheme.colors.status.error,
            fontSize: travelieTheme.typography.fontSize.sm
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {activeTab === 'details' && (
            <div style={{
              padding: travelieTheme.spacing.lg,
              flex: 1,
              overflow: 'auto'
            }}>
              <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                <label style={{
                  display: 'block',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.medium,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.xs
                }}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: travelieTheme.spacing.sm,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    border: `1px solid ${travelieTheme.colors.neutral.border}`,
                    borderRadius: travelieTheme.components.card.borderRadius,
                    fontFamily: 'inherit',
                    transition: `border-color ${travelieTheme.transitions.fast}`,
                    outline: 'none'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
                  onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: travelieTheme.spacing.md,
                marginBottom: travelieTheme.spacing.lg
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.primary,
                    marginBottom: travelieTheme.spacing.xs
                  }}>Travel Start Date</label>
                  <input
                    type="date"
                    value={formData.travel_start_date}
                    onChange={(e) => setFormData({ ...formData, travel_start_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: travelieTheme.spacing.sm,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      border: `1px solid ${travelieTheme.colors.neutral.border}`,
                      borderRadius: travelieTheme.components.card.borderRadius,
                      fontFamily: 'inherit',
                      transition: `border-color ${travelieTheme.transitions.fast}`,
                      outline: 'none'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
                    onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.primary,
                    marginBottom: travelieTheme.spacing.xs
                  }}>Travel End Date</label>
                  <input
                    type="date"
                    value={formData.travel_end_date}
                    onChange={(e) => setFormData({ ...formData, travel_end_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: travelieTheme.spacing.sm,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      border: `1px solid ${travelieTheme.colors.neutral.border}`,
                      borderRadius: travelieTheme.components.card.borderRadius,
                      fontFamily: 'inherit',
                      transition: `border-color ${travelieTheme.transitions.fast}`,
                      outline: 'none'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
                    onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
                  />
                </div>
              </div>

              <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                <label style={{
                  display: 'block',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.medium,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.xs
                }}>Agent Notes</label>
                <textarea
                  value={formData.agent_notes}
                  onChange={(e) => setFormData({ ...formData, agent_notes: e.target.value })}
                  rows="4"
                  placeholder="Internal notes about this itinerary..."
                  style={{
                    width: '100%',
                    padding: travelieTheme.spacing.sm,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    border: `1px solid ${travelieTheme.colors.neutral.border}`,
                    borderRadius: travelieTheme.components.card.borderRadius,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    transition: `border-color ${travelieTheme.transitions.fast}`,
                    outline: 'none'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
                  onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
                />
              </div>

              <div style={{
                padding: travelieTheme.spacing.md,
                background: travelieTheme.colors.neutral.background,
                borderRadius: travelieTheme.components.card.borderRadius,
                border: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>
                <h3 style={{
                  fontSize: travelieTheme.typography.fontSize.lg,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  margin: 0
                }}>Total Amount: ${calculateTotal().toFixed(2)}</h3>
              </div>
            </div>
          )}

          {activeTab === 'components' && (
            <div style={{
              padding: travelieTheme.spacing.lg,
              flex: 1,
              overflow: 'auto'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: travelieTheme.spacing.lg
              }}>
                <h3 style={{
                  fontSize: travelieTheme.typography.fontSize.lg,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  margin: 0
                }}>Travel Components</h3>
                <button 
                  type="button" 
                  onClick={handleAddComponent}
                  style={getButtonStyle('secondary')}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  Add Component
                </button>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: travelieTheme.spacing.md
              }}>
                {formData.components.map((component, index) => (
                  <div key={index} style={{
                    padding: travelieTheme.spacing.md,
                    background: travelieTheme.colors.neutral.background,
                    borderRadius: travelieTheme.components.card.borderRadius,
                    border: `1px solid ${travelieTheme.colors.neutral.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        fontSize: travelieTheme.typography.fontSize.md,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        color: travelieTheme.colors.text.primary,
                        margin: `0 0 ${travelieTheme.spacing.xs} 0`
                      }}>{componentTypes.find(t => t.id === component.type)?.icon} {component.title}</h4>
                      <p style={{
                        fontSize: travelieTheme.typography.fontSize.sm,
                        color: travelieTheme.colors.text.secondary,
                        margin: 0
                      }}>${parseFloat(component.price || 0).toFixed(2)}</p>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: travelieTheme.spacing.xs
                    }}>
                      <button 
                        type="button" 
                        onClick={() => handleEditComponent(component, index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: travelieTheme.spacing.xs,
                          transition: `transform ${travelieTheme.transitions.fast}`
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        ✏️
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleDeleteComponent(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: travelieTheme.spacing.xs,
                          transition: `transform ${travelieTheme.transitions.fast}`
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
                {formData.components.length === 0 && (
                  <p style={{
                    textAlign: 'center',
                    color: travelieTheme.colors.text.secondary,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    padding: travelieTheme.spacing.xl
                  }}>No components added yet.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'options' && (
            <div style={{
              padding: travelieTheme.spacing.lg,
              flex: 1,
              overflow: 'auto'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: travelieTheme.spacing.lg
              }}>
                <h3 style={{
                  fontSize: travelieTheme.typography.fontSize.lg,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  margin: 0
                }}>Package Options</h3>
                <button 
                  type="button" 
                  onClick={handleAddOption}
                  style={getButtonStyle('secondary')}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  Add Option
                </button>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: travelieTheme.spacing.md
              }}>
                {formData.options.map((option, index) => (
                  <div key={index} style={{
                    padding: travelieTheme.spacing.md,
                    background: travelieTheme.colors.neutral.background,
                    borderRadius: travelieTheme.components.card.borderRadius,
                    border: `1px solid ${travelieTheme.colors.neutral.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        fontSize: travelieTheme.typography.fontSize.md,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        color: travelieTheme.colors.text.primary,
                        margin: `0 0 ${travelieTheme.spacing.xs} 0`
                      }}>Option {String.fromCharCode(65 + index)}: {option.name}</h4>
                      <p style={{
                        fontSize: travelieTheme.typography.fontSize.sm,
                        color: travelieTheme.colors.text.secondary,
                        margin: `0 0 ${travelieTheme.spacing.xs} 0`
                      }}>{option.description}</p>
                      <p style={{
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.semibold,
                        color: travelieTheme.colors.text.primary,
                        margin: 0
                      }}>
                        Total: ${(option.components || []).reduce((sum, comp) => sum + (parseFloat(comp.price) || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: travelieTheme.spacing.xs
                    }}>
                      <button 
                        type="button" 
                        onClick={() => handleEditOption(option, index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: travelieTheme.spacing.xs,
                          transition: `transform ${travelieTheme.transitions.fast}`
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        ✏️
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleDeleteOption(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: travelieTheme.spacing.xs,
                          transition: `transform ${travelieTheme.transitions.fast}`
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
                {formData.options.length === 0 && (
                  <p style={{
                    textAlign: 'center',
                    color: travelieTheme.colors.text.secondary,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    padding: travelieTheme.spacing.xl
                  }}>No options added yet.</p>
                )}
              </div>
            </div>
          )}

          {activeTab !== 'supplier-payments' && activeTab !== 'client-payments' && activeTab !== 'portal-settings' && (
            <div style={{
              padding: travelieTheme.spacing.lg,
              borderTop: `1px solid ${travelieTheme.colors.neutral.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: travelieTheme.spacing.sm,
              flexShrink: 0
            }}>
              <button 
                type="button" 
                onClick={onClose}
                style={getButtonStyle('secondary')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading}
                style={{
                  ...getButtonStyle('primary'),
                  opacity: loading ? '0.5' : '1',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.currentTarget.style.opacity = '1';
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </form>

        {/* Supplier Payments Tab - Outside form to prevent submission conflicts */}
        {activeTab === 'supplier-payments' && (
          <div style={{
            padding: travelieTheme.spacing.lg,
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <SupplierPayments 
              itineraryId={itinerary?.id}
              totalAmount={calculateTotal()}
            />
            <div style={{
              padding: travelieTheme.spacing.lg,
              borderTop: `1px solid ${travelieTheme.colors.neutral.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 'auto'
            }}>
              <button 
                type="button" 
                onClick={onClose}
                style={getButtonStyle('secondary')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Client Payments Tab - Outside form to prevent submission conflicts */}
        {activeTab === 'client-payments' && (
          <div style={{
            padding: travelieTheme.spacing.lg,
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <ClientPayments 
              itineraryId={itinerary?.id}
              totalAmount={calculateTotal()}
            />
            <div style={{
              padding: travelieTheme.spacing.lg,
              borderTop: `1px solid ${travelieTheme.colors.neutral.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 'auto'
            }}>
              <button 
                type="button" 
                onClick={onClose}
                style={getButtonStyle('secondary')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Portal Settings Tab */}
        {activeTab === 'portal-settings' && (
          <div style={{
            padding: travelieTheme.spacing.lg,
            flex: 1,
            overflow: 'auto'
          }}>
            {!itinerary.quote_id ? (
              <div style={{
                padding: travelieTheme.spacing.md,
                background: '#FEF3C7',
                border: `2px solid ${travelieTheme.colors.status.warning}`,
                borderRadius: travelieTheme.components.card.borderRadius,
                color: '#92400E',
                fontSize: travelieTheme.typography.fontSize.sm
              }}>
                <p style={{ margin: 0 }}>⚠️ This itinerary is not linked to a quote. Portal features are only available for quote-based itineraries.</p>
              </div>
            ) : (
              <div className="portal-settings-tab">
                {/* Portal Status Section */}
                <div className="portal-status-section">
                  <h3>🔗 Client Portal</h3>
                  <div className="portal-info-box">
                    <div className="portal-status">
                      <span className="status-label">Portal Status:</span>
                      <span className={`status-badge ${portalUrl ? 'active' : 'inactive'}`}>
                        {portalUrl ? '✅ Active' : '⚠️ Not Generated'}
                      </span>
                    </div>
                    {portalUrl && (
                      <div className="portal-url">
                        <span className="url-label">Portal Link:</span>
                        <input 
                          type="text" 
                          value={portalUrl} 
                          readOnly 
                          className="portal-url-input"
                        />
                      </div>
                    )}
                    <div className="portal-actions">
                      <button 
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSendPortalLink}
                        disabled={sendingPortalLink}
                      >
                        {sendingPortalLink ? 'Sending...' : '📧 Send to Client'}
                      </button>
                      {portalUrl && (
                        <>
                          <button 
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCopyPortalLink}
                          >
                            📋 Copy Link
                          </button>
                          <button 
                            type="button"
                            className="btn btn-info"
                            onClick={handlePreviewPortal}
                          >
                            👁️ Preview Portal
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Checklist Editor Section */}
                <div className="checklist-editor-section">
                  <h3>✅ Checklist Editor</h3>
                  <p className="section-description">
                    Customize the checklist that clients will see in their portal. Add, edit, or remove items to match this specific trip.
                  </p>
                  
                  <div className="checklist-categories">
                    {portalChecklist.map((category, catIndex) => (
                      <div key={catIndex} className="checklist-category">
                        <div className="category-header">
                          <input
                            type="text"
                            value={category.category}
                            onChange={(e) => handleUpdateCategoryName(catIndex, e.target.value)}
                            className="category-name-input"
                          />
                          <button
                            type="button"
                            className="btn-icon btn-danger-icon"
                            onClick={() => handleDeleteCategory(catIndex)}
                            title="Delete category"
                          >
                            🗑️
                          </button>
                        </div>
                        
                        <div className="checklist-items">
                          {category.items.map((item, itemIndex) => (
                            <div key={item.id} className="checklist-item-editor">
                              <span className="item-number">{itemIndex + 1}.</span>
                              <input
                                type="text"
                                value={item.text}
                                onChange={(e) => handleUpdateChecklistItem(catIndex, itemIndex, e.target.value)}
                                className="item-text-input"
                                placeholder="Enter checklist item..."
                              />
                              <button
                                type="button"
                                className="btn-icon btn-danger-icon"
                                onClick={() => handleDeleteChecklistItem(catIndex, itemIndex)}
                                title="Delete item"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary add-item-btn"
                            onClick={() => handleAddChecklistItem(catIndex)}
                          >
                            + Add Item
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    type="button"
                    className="btn btn-secondary add-category-btn"
                    onClick={handleAddChecklistCategory}
                  >
                    + Add Category
                  </button>
                </div>

                {/* Detailed Schedule Editor */}
                <div className="schedule-editor-section">
                  <h3>📅 Detailed Schedule (Optional)</h3>
                  <p className="schedule-description">
                    Create a day-by-day schedule with multiple events per day. Perfect for cruises, tours, and complex itineraries.
                    If enabled, this will override the component-based schedule in the client portal.
                  </p>
                  
                  <div className="schedule-toggle">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={useDetailedSchedule}
                        onChange={(e) => setUseDetailedSchedule(e.target.checked)}
                      />
                      <span>Use detailed schedule (overrides components)</span>
                    </label>
                  </div>

                  {useDetailedSchedule && (
                    <div className="schedule-days">
                      {portalSchedule.map((day, dayIndex) => (
                        <div key={dayIndex} className="schedule-day">
                          <div className="day-header">
                            <div className="day-inputs">
                              <input
                                type="date"
                                value={day.date}
                                onChange={(e) => handleUpdateScheduleDay(dayIndex, 'date', e.target.value)}
                                className="day-date-input"
                              />
                              <input
                                type="text"
                                value={day.title}
                                onChange={(e) => handleUpdateScheduleDay(dayIndex, 'title', e.target.value)}
                                className="day-title-input"
                                placeholder="Day title (e.g., Santorini, Greece)"
                              />
                            </div>
                            <button
                              type="button"
                              className="btn-icon btn-danger-icon"
                              onClick={() => handleDeleteScheduleDay(dayIndex)}
                              title="Delete day"
                            >
                              🗑️
                            </button>
                          </div>

                          {/* Photo URL Input */}
                          <div className="day-photo-section">
                            <label className="photo-label">
                              📷 Day Photo (Optional)
                            </label>
                            <div className="photo-input-group">
                              {day.photo && (
                                <img 
                                  src={day.photo} 
                                  alt="Day preview"
                                  className="photo-preview"
                                />
                              )}
                              <input
                                type="text"
                                value={day.photo || ''}
                                onChange={(e) => handleUpdateScheduleDay(dayIndex, 'photo', e.target.value)}
                                className="photo-url-input"
                                placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                              />
                            </div>
                            <p className="photo-hint">
                              💡 Tip: Upload to your media library or use a direct image URL. This will override component photos.
                            </p>
                          </div>

                          <div className="schedule-events">
                            {day.events.map((event, eventIndex) => (
                              <div key={eventIndex} className="schedule-event-editor">
                                <input
                                  type="time"
                                  value={event.time}
                                  onChange={(e) => handleUpdateScheduleEvent(dayIndex, eventIndex, 'time', e.target.value)}
                                  className="event-time-input"
                                />
                                <input
                                  type="text"
                                  value={event.description}
                                  onChange={(e) => handleUpdateScheduleEvent(dayIndex, eventIndex, 'description', e.target.value)}
                                  className="event-description-input"
                                  placeholder="Event description..."
                                />
                                <button
                                  type="button"
                                  className="btn-icon btn-danger-icon"
                                  onClick={() => handleDeleteScheduleEvent(dayIndex, eventIndex)}
                                  title="Delete event"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary add-event-btn"
                              onClick={() => handleAddScheduleEvent(dayIndex)}
                            >
                              + Add Event
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        className="btn btn-secondary add-day-btn"
                        onClick={handleAddScheduleDay}
                      >
                        + Add Day
                      </button>
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div style={{
                  marginTop: travelieTheme.spacing.lg,
                  paddingTop: travelieTheme.spacing.lg,
                  borderTop: `1px solid ${travelieTheme.colors.neutral.border}`,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: travelieTheme.spacing.sm
                }}>
                  <button 
                    type="button"
                    onClick={handleSavePortalSettings}
                    disabled={loading}
                    style={{
                      ...getButtonStyle('success'),
                      opacity: loading ? '0.5' : '1',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) e.currentTarget.style.opacity = '1';
                    }}
                  >
                    {loading ? 'Saving...' : '💾 Save Portal Settings'}
                  </button>
                  <button 
                    type="button" 
                    onClick={onClose}
                    style={getButtonStyle('secondary')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Component Modal */}
        {showComponentModal && (
          <ComponentModal
            isOpen={showComponentModal}
            onClose={() => setShowComponentModal(false)}
            onSave={handleSaveComponent}
            component={editingComponent}
          />
        )}

        {/* Option Modal */}
        {showOptionModal && (
          <OptionModal
            isOpen={showOptionModal}
            onClose={() => setShowOptionModal(false)}
            onSave={handleSaveOption}
            option={editingOption}
          />
        )}
    </Modal>
  );
};

// Component Modal - Themed. Hosts the same type picker + per-type forms
// (FlightForm, HotelForm, etc. via componentFormRegistry.js) that
// QuoteBuilder.js uses, so an itinerary component edited here keeps its
// full data (dates, airports, tripType, isOption, photos, etc.) instead of
// being reduced to a bare Type/Title/Description/Price shell.
const ComponentModal = ({ isOpen, onClose, onSave, component }) => {
  // Track the selected type locally so switching it (or picking one for a
  // brand-new component) re-renders the right form without round-tripping
  // through the parent.
  const [selectedType, setSelectedType] = useState(component?.type || '');

  useEffect(() => {
    setSelectedType(component?.type || '');
  }, [component]);

  const getButtonStyle = (type) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`
    };
    return type === 'primary' 
      ? { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' }
      : { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary };
  };

  // Forms don't set `type` themselves (see componentFormRegistry.js),
  // so it has to be threaded through here from whichever type button/
  // pre-existing component selected it.
  const handleFormSave = (formData) => {
    onSave({ ...formData, type: formData.type || selectedType });
  };

  const componentLabel = componentTypes.find(t => t.id === selectedType)?.name;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${component ? 'Edit' : 'Add'} ${componentLabel || 'Component'}`}
      maxWidth={selectedType ? '700px' : '560px'}
    >
          {!selectedType ? (
            <div>
              {componentCategories.map(category => (
                <div key={category.id} style={{ marginBottom: travelieTheme.spacing.lg }}>
                  <h4 style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.text.secondary,
                    margin: `0 0 ${travelieTheme.spacing.sm} 0`
                  }}>{category.icon} {category.name}</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: travelieTheme.spacing.sm }}>
                    {category.components.map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setSelectedType(type.id)}
                        style={{
                          ...getButtonStyle('secondary'),
                          display: 'flex',
                          alignItems: 'center',
                          gap: travelieTheme.spacing.xs
                        }}
                      >
                        <span>{type.icon}</span>
                        <span>{type.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            renderComponentTypeForm(selectedType, {
              onSave: handleFormSave,
              onCancel: onClose,
              initialData: getComponentFormInitialData(component)
            })
          )}
    </Modal>
  );
};

// Option Modal - Themed
const OptionModal = ({ isOpen, onClose, onSave, option }) => {
  const [formData, setFormData] = useState({
    name: option?.name || '',
    description: option?.description || '',
    components: option?.components || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const getButtonStyle = (type) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`
    };
    return type === 'primary' 
      ? { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' }
      : { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary };
  };

  const inputStyle = {
    width: '100%',
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    fontFamily: 'inherit',
    transition: `border-color ${travelieTheme.transitions.fast}`,
    outline: 'none'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={option ? 'Edit Option' : 'Add Option'} maxWidth="500px">
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: travelieTheme.spacing.md }}>
            <label style={{
              display: 'block',
              fontSize: travelieTheme.typography.fontSize.sm,
              fontWeight: travelieTheme.typography.fontWeight.medium,
              color: travelieTheme.colors.text.primary,
              marginBottom: travelieTheme.spacing.xs
            }}>Option Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Budget Package, Luxury Package"
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: travelieTheme.spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: travelieTheme.typography.fontSize.sm,
              fontWeight: travelieTheme.typography.fontWeight.medium,
              color: travelieTheme.colors.text.primary,
              marginBottom: travelieTheme.spacing.xs
            }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Describe what makes this option unique..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: travelieTheme.spacing.sm,
            paddingTop: travelieTheme.spacing.md,
            borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
          }}>
            <button 
              type="button" 
              onClick={onClose}
              style={getButtonStyle('secondary')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Cancel
            </button>
            <button 
              type="submit"
              style={getButtonStyle('primary')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Save
            </button>
          </div>
        </form>
    </Modal>
  );
};

export default EditItineraryModal;
